import React, { FC, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import config from 'src/shared/config';
import { T } from 'src/shared/helpers';

import { EmptyState, LearnMoreLink, StructureSection, StructureSectionHeader, StructureSectionTitle } from '../common/styled';
import type { viewerStructureSection_ViewerIframeQuery } from './__generated__/viewerStructureSection_ViewerIframeQuery.graphql';
import styled from 'styled-components';

const viewerIframeQuery = graphql`
    query viewerStructureSection_ViewerIframeQuery($id: ID!) {
        node(id: $id) {
            ... on Model {
                modelID
                iframeUrl {
                    viewerUrl
                }
            }
        }
    }
`;

const CodeBlock = styled.div`
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px 12px;
    font-family: 'SF Mono', 'Fira Code', 'Fira Mono', Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 1.5;
    word-break: break-all;
    white-space: pre-wrap;
    color: #374151;
    margin-top: 8px;
`;

const CopyButton = styled.button`
    background: none;
    border: none;
    color: #3b82f6;
    cursor: pointer;
    font-size: 12px;
    padding: 4px 0;
    margin-top: 4px;
    &:hover {
        text-decoration: underline;
    }
`;

const FieldLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    margin-top: 12px;
    &:first-child {
        margin-top: 0;
    }
`;

export interface ViewerStructureSectionProps {
    productId: string;
}

export const ViewerStructureSection: FC<ViewerStructureSectionProps> = ({ productId }) => {
    const data = useLazyLoadQuery<viewerStructureSection_ViewerIframeQuery>(viewerIframeQuery, { id: productId }, { fetchPolicy: 'store-or-network' });

    const viewerUrl = data.node?.iframeUrl?.viewerUrl;
    const modelID = data.node?.modelID;
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        });
    };

    const damUrl = '/' + config.culture + '/Admin/DAM?page=/gallery';

    const iframeCode = viewerUrl
        ? `<iframe src="${viewerUrl}" frameborder="0" width="100%" height="600px" allow="fullscreen; accelerometer; magnetometer; autoplay; encrypted-media; gyroscope; picture-in-picture; camera *; xr-spatial-tracking;"></iframe>`
        : '';

    return (
        <>
            <StructureSection>
                <StructureSectionHeader>
                    <StructureSectionTitle>{T._('3D Asset', 'Admin')}</StructureSectionTitle>
                </StructureSectionHeader>
                <EmptyState>
                    {T._('This product is managed in the', 'Admin')}{' '}
                    <LearnMoreLink as='span' onClick={() => (window.location.href = damUrl)} style={{ cursor: 'pointer' }}>
                        {T._('3D DAM Asset Library', 'Admin')} &#8250;
                    </LearnMoreLink>
                </EmptyState>
            </StructureSection>

            {viewerUrl && (
                <StructureSection>
                    <StructureSectionHeader>
                        <StructureSectionTitle>{T._('Share & Embed 3D', 'Admin')}</StructureSectionTitle>
                    </StructureSectionHeader>
                    <EmptyState>
                        <FieldLabel>{T._('Link', 'Admin')}</FieldLabel>
                        <CodeBlock>{viewerUrl}</CodeBlock>
                        <CopyButton onClick={() => handleCopy(viewerUrl, 'link')}>
                            {copiedField === 'link' ? T._('Copied!', 'Admin') : T._('Copy link', 'Admin')}
                        </CopyButton>

                        <FieldLabel>{T._('Iframe code', 'Admin')}</FieldLabel>
                        <CodeBlock>{iframeCode}</CodeBlock>
                        <CopyButton onClick={() => handleCopy(iframeCode, 'iframe')}>
                            {copiedField === 'iframe' ? T._('Copied!', 'Admin') : T._('Copy HTML', 'Admin')}
                        </CopyButton>
                    </EmptyState>
                </StructureSection>
            )}
        </>
    );
};
